import { useMemo } from 'react'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import { Ingredient, IngredientReplacement } from '@ancon/wildcat-types'
import { useController, useFormContext } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'

import useAppSelector from '../../../store/hooks/useAppSelector'
import { SelectableType } from '../../app/components/Selectable'
import ProductConfigurationAccordion from '../components/ProductConfigurationAccordion'
import ProductSelection from '../components/ProductSelection'
import {
  productDetailsVariantByIdSelector,
  productsDetailsAllowDynamicPricingSelector,
  productsDetailsIngredientsSelector,
  productsDetailsVariantsSelector,
} from '../store/productSelectors'
import { ConfigureProductFormState } from '../types'
import { ProductConfigureAccordionId } from '../constants'
import getProductDetailsCurrency from '../utils/getProductDetailsCurrency'

import styles from './ProductIngredientsForm.module.scss'

type ProductIngredientProp = {
  index: number
  ingredient: Ingredient
  currency: string | undefined
}

function ProductIngredient({
  index,
  ingredient,
  currency: productCurrency,
}: ProductIngredientProp) {
  const { setValue } = useFormContext<ConfigureProductFormState>()
  const allowDynamicPricing = useAppSelector(
    productsDetailsAllowDynamicPricingSelector,
  )
  const {
    ingredientId: mainIngredientId,
    ingredientName,
    hasReplacements,
    price: mainIngredientPrice,
    isMandatory,
    replacements,
    position,
  } = ingredient

  const {
    field: { value: selectedValue, onChange },
  } = useController({
    name: `selectedIngredients.${index}.selected`,
  })
  const {
    field: { value: replacementIdValue },
  } = useController({
    name: `selectedIngredients.${index}.replacementId`,
  })

  function handleCheckChange(checked: boolean) {
    setValue(`selectedIngredients.${index}.selected`, checked)
    onChange(checked)

    if (!checked) {
      setValue(`selectedIngredients.${index}.replacementId`, null)
    }
  }

  function handleReplacementChange(replacementId: string) {
    const isMainIngredient = replacementId === mainIngredientId

    setValue(`selectedIngredients.${index}`, {
      id: mainIngredientId,
      selected: true,
      replacementId: isMainIngredient ? null : replacementId,
      position,
    })
    onChange(true)
  }

  const replacementsList = useMemo(
    () =>
      [
        {
          ingredientId: mainIngredientId,
          ingredientName,
          currency: productCurrency,
        } as IngredientReplacement,
      ].concat(replacements),
    [mainIngredientId, replacements, ingredientName, productCurrency],
  )

  return (
    <>
      <ProductSelection
        label={ingredientName}
        onChange={handleCheckChange}
        selected={isMandatory || selectedValue}
        selectionType={SelectableType.CheckBox}
        disabled={isMandatory}
        priceLabel={
          allowDynamicPricing && !hasReplacements
            ? // FIXME: Update Ingredient API to return currency, and use it instead of the product currency
              getFormattedCurrency(mainIngredientPrice, productCurrency)
            : undefined
        }
      />
      {hasReplacements && (
        <div className={styles.replacementsContainer}>
          {replacementsList.map(
            ({
              ingredientId: replacementIngredientId,
              ingredientName: replacementName,
              price,
              currency,
            }) => (
              <ProductSelection
                key={replacementIngredientId}
                label={replacementName}
                onChange={() =>
                  handleReplacementChange(replacementIngredientId)
                }
                selected={
                  (selectedValue
                    ? replacementIdValue || mainIngredientId
                    : undefined) === replacementIngredientId
                }
                selectionType={SelectableType.RadioButton}
                priceLabel={
                  (allowDynamicPricing &&
                    replacementIngredientId === mainIngredientId) ||
                  replacementIngredientId !== mainIngredientId
                    ? getFormattedCurrency(
                        replacementIngredientId !== mainIngredientId
                          ? price
                          : mainIngredientPrice,
                        currency,
                      )
                    : undefined
                }
              />
            ),
          )}
        </div>
      )}
    </>
  )
}

export default function ProductIngredientsForm() {
  const { t } = useTranslation('common')

  const { watch, setValue } = useFormContext<ConfigureProductFormState>()

  const [activeAccordionId, selectedVariantId] = watch([
    'activeAccordionId',
    'selectedVariantId',
  ])

  const ingredients = useAppSelector(productsDetailsIngredientsSelector)!
  const variants = useAppSelector(productsDetailsVariantsSelector)
  const selectedVariant = useAppSelector(state =>
    productDetailsVariantByIdSelector(state, selectedVariantId),
  )
  const currency = getProductDetailsCurrency(variants, selectedVariant)

  function handleToggleAccordion(isExpanded: boolean) {
    if (isExpanded) {
      const addonAccordionId = ProductConfigureAccordionId.Ingredients
      setValue('activeAccordionId', addonAccordionId)
    }
  }

  return (
    <ProductConfigurationAccordion
      title={t('components.productDetailsModal.choiceOfIngredients')}
      sectionId={ProductConfigureAccordionId.Ingredients}
      expanded={activeAccordionId === ProductConfigureAccordionId.Ingredients}
      onToggle={handleToggleAccordion}
    >
      {ingredients.map((ingredient, index) => (
        <ProductIngredient
          ingredient={ingredient}
          index={index}
          currency={currency}
          key={ingredient.id}
        />
      ))}
    </ProductConfigurationAccordion>
  )
}
